import { ApolloProvider } from '@apollo/client'
import type { NextPage } from 'next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { ReactElement, ReactNode } from 'react'
import { ThemeProvider, createGlobalStyle } from 'styled-components'
// see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31245#issuecomment-446011384
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as types from 'styled-components/cssprop'

import { SegmentScript } from '@/components/atoms/SegmentScript'
import { CONSTANTS, defaultTheme } from '@/constants'
import { useApollo } from '@/lib/apollo'

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const { DHARMA_NAME } = CONSTANTS

const GlobalStyle = createGlobalStyle`
  body,
  #__next {
    width: 100%;
    height: 100%;
  }

  // Necessary to not have scroll bar space disappear when locking modals from scrolling
  body {
    overflow-y: scroll;
  }

  html,
  body {
    font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen,
      Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  // CSS Reset

  * {
    box-sizing: border-box;
  }

  /* Box sizing rules */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  /* Remove default margin */
  body,
  h1,
  h2,
  h3,
  h4,
  p,
  figure,
  blockquote,
  dl,
  dd {
    margin: 0;
  }

  /* Remove list styles on ul, ol elements with a list role, which suggests default styling will be removed */
  ul[role='list'],
  ol[role='list'] {
    list-style: none;
  }

  /* Set core root defaults */
  html:focus-within {
    scroll-behavior: auto;
  }

  /* Set core body defaults */
  body {
    min-height: 100vh;
    line-height: 1.5;
  }

  /* A elements that don't have a class get default styles */
  a:not([class]) {
    text-decoration-skip-ink: auto;
  }

  /* Make images easier to work with */
  img,
  picture {
    max-width: 100%;
    display: block;
  }

  /* Disable blue highlight of button etc. clicks on mobile */
  input,
  textarea,
  button,
  select,
  a {
    -webkit-tap-highlight-color: transparent;
  }

  /* Inherit fonts for inputs and buttons */
  input,
  button,
  textarea,
  select {
    font: inherit;
  }

  /* Remove all animations and transitions for people that prefer not to see them */
  @media (prefers-reduced-motion: reduce) {
    html:focus-within {
      scroll-behavior: auto;
    }
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`

const HeadSection = () => (
  <Head>
    <title>{DHARMA_NAME}</title>

    {/* Need to verify site ownership to track organic search queries that lead to site visits /*}
    {/* See: https://support.google.com/webmasters/answer/9008080#meta_tag_verification */}
    <meta
      name="google-site-verification"
      content="bFp-HAdYRNNEhrsUWzU3gDpbufk8xMkIzMv99KqcwQ8"
    />

    <meta
      name="description"
      content="Buy more than 70,000 crypto tokens with USD instantly. Always get the best rate on Ethereum or Polygon with Dharma."
    />
    <meta name="twitter:card" content="summary_large_image" />
    <meta
      property="twitter:image"
      content="https://dharma.io/assets/images/og_image.png"
    />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="Dharma" />
    <meta
      property="og:image"
      content="https://dharma.io/assets/images/og_image.png"
    />
    <meta property="og:description" content="Dollars to DeFi in a Tap" />
    <meta property="og:title" content="Dharma — Your Ethereum Wallet" />
    <meta property="og:url" content="https://dharma.io" />

    <link rel="icon" href="/favicon.ico" />
    <link
      rel="preload"
      href="/assets/fonts/Circular/CircularXXWeb-Book.woff"
      as="font"
      type="font/woff"
    />
    <link
      rel="preload"
      href="/assets/fonts/Circular/CircularXXWeb-Book.woff2"
      as="font"
      type="font/woff2"
    />
    <link
      rel="preload"
      href="/assets/fonts/Circular/CircularXXWeb-Medium.woff"
      as="font"
      type="font/woff"
    />
    <link
      rel="preload"
      href="/assets/fonts/Circular/CircularXXWeb-Medium.woff2"
      as="font"
      type="font/woff2"
    />
    <link
      rel="preload"
      href="/assets/fonts/Circular/CircularXXMonoWeb-Regular.woff"
      as="font"
      type="font/woff"
    />
    <link
      rel="preload"
      href="/assets/fonts/Circular/CircularXXMonoWeb-Regular.woff2"
      as="font"
      type="font/woff2"
    />
    <link
      rel="preload"
      href="/assets/fonts/Circular/CircularXXMonoWeb-Regular.woff2"
    />
    <link href="/assets/fonts/Circular/circularFont.css" rel="stylesheet" />
    <script
      id="ze-snippet"
      src="https://static.zdassets.com/ekr/snippet.js?key=6f4e0f65-7dbe-4c13-b2cf-7bbd54ec6a81"
    />
  </Head>
)

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page)
  const apolloClient = useApollo(pageProps.initialApolloState)

  return (
    <>
      <HeadSection />
      {/* nextjs/script Scripts must not be defined in Head, so included here */}
      <SegmentScript />
      <GlobalStyle />
      <ApolloProvider client={apolloClient}>
        <ThemeProvider theme={defaultTheme}>
          {getLayout(<Component {...pageProps} />)}
        </ThemeProvider>
      </ApolloProvider>
    </>
  )
}
